import { useEffect, useState } from 'react';
import { CONSENT_EVENT, openConsentPreferences, readConsent } from '@/utils/consent';

interface MapEmbedProps {
  /** URL de incorporação do Google Maps (só é carregada depois do consentimento). */
  src: string;
  title: string;
  /** Texto usado para o link "Abrir no Google Maps". */
  fallbackQuery: string;
  className?: string;
}

/**
 * Mapa do Google com consentimento. Antes do "sim" nenhuma requisição ao Google é feita:
 * mostramos um aviso, um botão para carregar só este mapa e um link para abrir no Maps.
 */
export default function MapEmbed({ src, title, fallbackQuery, className }: MapEmbedProps) {
  const [allowed, setAllowed] = useState<boolean>(() => {
    const c = readConsent();
    return !!c && c.thirdParty;
  });
  const [loadOnce, setLoadOnce] = useState(false);

  useEffect(() => {
    const onChange = () => {
      const c = readConsent();
      setAllowed(!!c && c.thirdParty);
    };
    window.addEventListener(CONSENT_EVENT, onChange);
    return () => window.removeEventListener(CONSENT_EVENT, onChange);
  }, []);

  if (allowed || loadOnce) {
    return (
      <iframe
        src={src}
        title={title}
        className={className || 'w-full h-full border-0'}
        loading="lazy"
        allowFullScreen
        referrerPolicy="no-referrer-when-downgrade"
      ></iframe>
    );
  }

  const href = 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(fallbackQuery);

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-gray-100 dark:bg-gray-800 p-6 text-center">
      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/20 text-primary-800 dark:text-primary">
        <i className="ri-map-pin-2-line text-2xl" aria-hidden="true"></i>
      </div>
      <p className="max-w-md font-body text-sm leading-relaxed text-gray-700 dark:text-gray-200">
        O mapa é um serviço do Google e só é carregado com a sua permissão, porque o Google pode registrar seu IP
        e usar cookies.
      </p>
      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          onClick={() => setLoadOnce(true)}
          className="inline-flex items-center justify-center rounded-full bg-primary px-5 py-2.5 font-body text-sm font-semibold text-gray-900 hover:bg-primary-400 cursor-pointer whitespace-nowrap"
        >
          Carregar o mapa agora
        </button>
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center rounded-full border border-gray-300 dark:border-gray-600 px-5 py-2.5 font-body text-sm font-semibold text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700 whitespace-nowrap"
        >
          Abrir no Google Maps
        </a>
      </div>
      <button
        type="button"
        onClick={openConsentPreferences}
        className="font-body text-xs text-gray-600 dark:text-gray-300 underline cursor-pointer"
      >
        Permitir conteúdo de terceiros em todo o site
      </button>
    </div>
  );
}
